import { useCallback, useEffect, useState } from 'react'

/**
 * The install prompt is not something a page can simply open. Chromium fires
 * `beforeinstallprompt` when it decides the app qualifies, and that event is
 * the only way to show the dialog — so it has to be captured and kept.
 *
 * Safari fires nothing at all: on iOS the only route is Share → Add to Home
 * Screen, which is why `needsManualSteps` exists rather than a button that
 * would do nothing.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari's non-standard flag, still the only signal on iOS.
    (navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

export function isIos(): boolean {
  return (
    /iP(hone|ad|od)/.test(navigator.userAgent) ||
    // iPadOS reports as a Mac; the touch points give it away.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

export function useInstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState(isStandalone)

  useEffect(() => {
    const onPrompt = (e: Event) => {
      // Stopping the default keeps Chrome's own banner from appearing, so the
      // invitation happens where we choose rather than over the sign-in form.
      e.preventDefault()
      setDeferred(e as BeforeInstallPromptEvent)
    }
    const onInstalled = () => {
      setInstalled(true)
      setDeferred(null)
    }
    window.addEventListener('beforeinstallprompt', onPrompt)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const install = useCallback(async () => {
    if (!deferred) return 'unavailable' as const
    await deferred.prompt()
    const { outcome } = await deferred.userChoice
    // The event is single-use: a dismissed prompt cannot be reopened until the
    // browser fires a fresh one.
    setDeferred(null)
    return outcome
  }, [deferred])

  return {
    /** True when a real install dialog can be opened right now. */
    canInstall: !installed && deferred !== null,
    /** iOS has no programmatic prompt, so show the manual steps instead. */
    needsManualSteps: !installed && deferred === null && isIos(),
    installed,
    install,
  }
}
